/**
 * Owner tokens in localStorage. They are the only proof of ownership an
 * anonymous calendar has, so losing this store loses the calendar — the SPA
 * shows the token once as a link and says so.
 *
 * The key shape and the guarded storage accessor come from `core/api`, which
 * reads the same entries when it attaches a Bearer token.
 */
import { ownerTokenKey, readOwnerToken, safeStorage } from '../core/api';

export const OWNER_PREFIX = 'circle:owner:';

/**
 * What the front page remembers about a calendar next to its key, so the list
 * of rings this device holds can be drawn without fetching every one of them.
 * `circle:owner-note:<id>`; the hyphen keeps it out of {@link ownerTokens.list}.
 */
const NOTE_PREFIX = 'circle:owner-note:';

export interface StoredOwnerToken {
  calendarId: string;
  token: string;
}

export interface OwnerNote {
  name: string;
  claimed: boolean;
}

export const ownerTokens = {
  get(calendarId: string): string | undefined {
    return readOwnerToken(calendarId) ?? undefined;
  },

  set(calendarId: string, token: string): void {
    try {
      safeStorage()?.setItem(ownerTokenKey(calendarId), token);
    } catch {
      // Nothing to do: the caller has already shown the owner link.
    }
  },

  remove(calendarId: string): void {
    try {
      safeStorage()?.removeItem(ownerTokenKey(calendarId));
      safeStorage()?.removeItem(NOTE_PREFIX + calendarId);
    } catch {
      /* ignore */
    }
  },

  /** The name and claim state last seen for a calendar, if this device has one. */
  note(calendarId: string): OwnerNote | null {
    try {
      const raw = safeStorage()?.getItem(NOTE_PREFIX + calendarId);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as Partial<OwnerNote>;
      return typeof parsed.name === 'string'
        ? { name: parsed.name, claimed: parsed.claimed === true }
        : null;
    } catch {
      return null;
    }
  },

  /** Written whenever a bundle is in hand, so the front page's list stays true. */
  setNote(calendarId: string, note: OwnerNote): void {
    try {
      safeStorage()?.setItem(NOTE_PREFIX + calendarId, JSON.stringify(note));
    } catch {
      /* the front page fetches instead */
    }
  },

  /** Every calendar this device holds a key for. */
  list(): StoredOwnerToken[] {
    const store = safeStorage();
    if (!store) return [];
    const out: StoredOwnerToken[] = [];
    try {
      for (let i = 0; i < store.length; i++) {
        const key = store.key(i);
        if (!key?.startsWith(OWNER_PREFIX)) continue;
        const token = store.getItem(key);
        if (token) out.push({ calendarId: key.slice(OWNER_PREFIX.length), token });
      }
    } catch {
      return out;
    }
    return out;
  },
};

/** `/o/<calendarId>/<ownerToken>` — the link that is the key. */
export function ownerLink(calendarId: string, token: string, origin = location.origin): string {
  return `${origin}/o/${calendarId}/${token}`;
}
